/*
 * Signal — product cross-links in rich text.
 * ---------------------------------------------------------------------------
 * Most product descriptions point at a sibling product ("single packs also
 * available"), written in the admin as an ordinary absolute link. This upgrades
 * those links into a small product card: thumbnail, title, price.
 *
 * PROGRESSIVE ENHANCEMENT. The authored link is the product. Nothing is removed
 * until the product JSON is in hand, so with scripting off, on a failed fetch,
 * on a 404, or on a handle that has since been deleted, the description keeps
 * the link the merchant wrote and it keeps working.
 *
 * SAME-ORIGIN. The links are authored as absolute https://materialthings.ie
 * URLs, which resolve to nothing on the dev server, on a preview theme, or on
 * the permanent myshopify domain. Only the path is used: the handle is lifted
 * out of it and refetched against whatever origin the page is actually being
 * served from, through Shopify's native /products/<handle>.js endpoint.
 *
 * This element renders nothing itself. It is emitted once per product page by
 * snippets/signal-product-link.liquid and scans the rich-text containers on the
 * page, because the description block is stock Horizon markup that Signal does
 * not own and therefore cannot wrap.
 *
 * A card for something that cannot be bought carries its state and a notify-me
 * button rather than a price — a price on something unbuyable reads as an offer.
 * The button is a trigger for the shared dialog (assets/signal-notify.js), given
 * the same data attributes snippets/signal-notify-trigger.liquid writes in
 * Liquid, so the collection grid and this card raise the same capture. Which of
 * the two unbuyable states applies comes from the release map the Liquid side
 * resolves; see that snippet for why it cannot be read from the product JSON.
 */

/**
 * The shape this uses out of Shopify's product JSON. The endpoint returns far
 * more than this; everything else is ignored.
 *
 * @typedef {object} ProductJson
 * @property {string} title
 * @property {number} price - Minor units of the shop's currency.
 * @property {boolean} [available]
 * @property {boolean} [price_varies]
 * @property {string | null} [featured_image]
 * @property {{ id?: number, sku?: string | null }[]} [variants]
 */

// Rich text only. Nav, footer and card links are not description copy and are
// left alone even when they point at a product.
const SCOPE_SELECTOR = 'rte-formatter, .rte';

// Wrappers a link may be lifted out of when it is their only content, so that
// `<p><strong><a>…</a></strong></p>` leaves no empty shell behind. Deliberately
// excludes list items, table cells and headings: replacing those would either
// produce invalid markup or lose structure.
const UNWRAPPABLE = new Set(['P', 'STRONG', 'B', 'EM', 'I', 'SPAN', 'DIV']);

// Product handles are ASCII words and hyphens. Anything else is not a handle we
// wrote, and is left as a plain link rather than fetched.
const HANDLE_PATTERN = /^[\w-]+$/;

const THUMBNAIL_WIDTH = 128;

class SignalProductLink extends HTMLElement {
  /** @type {AbortController | undefined} */
  abortController;

  /**
   * Keyed by product path, so a handle linked twice in one description is
   * fetched once and both links resolve from the same request.
   *
   * @type {Map<string, Promise<ProductJson | null>>}
   */
  requests = new Map();

  /** @type {string} */
  currency = '';

  /** @type {string} */
  rootUrl = '/';

  /** @type {string} */
  shopUrl = '';

  /** @type {Set<string>} */
  shopHosts = new Set();

  /**
   * Handles still ahead of their release date, buyable or not, mapped to
   * [machine date, worded date]. For an unbuyable one, absent means sold out —
   * see the snippet.
   *
   * @type {Record<string, [string, string]>}
   */
  releases = {};

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.currency = this.dataset.signalCurrency ?? '';
    this.rootUrl = this.dataset.signalRoot || '/';
    this.shopUrl = this.dataset.signalShopUrl ?? '';

    try {
      this.releases = JSON.parse(this.dataset.signalReleases || '{}');
    } catch (error) {
      // A malformed map costs the street dates, not the cards: every unbuyable
      // product then reads as sold out, which is the commoner of the two states.
      this.releases = {};
    }

    this.shopHosts = new Set([window.location.host.toLowerCase()]);
    (this.dataset.signalShopDomains ?? '')
      .split(',')
      .map((host) => host.trim().toLowerCase())
      .filter(Boolean)
      .forEach((host) => this.shopHosts.add(host));

    this.upgradeLinks();

    // The description lives in a different section from this element, so a
    // theme-editor save re-renders the description without re-running this.
    document.addEventListener('shopify:section:load', () => this.upgradeLinks(), {
      signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.requests.clear();

    delete this.dataset.initialized;
  }

  upgradeLinks() {
    document.querySelectorAll(SCOPE_SELECTOR).forEach((container) => {
      container
        .querySelectorAll('a[href*="/products/"]')
        .forEach((link) => this.upgradeLink(/** @type {HTMLAnchorElement} */ (link)));
    });
  }

  /** @param {HTMLAnchorElement} link */
  upgradeLink(link) {
    if (link.dataset.signalProductLink) return;

    const path = this.productPath(link.href);
    if (!path) return;

    link.dataset.signalProductLink = 'pending';

    this.fetchProduct(path)
      .then((product) => {
        // A null product is the ordinary miss — network error, 404, deleted
        // handle, or teardown mid-flight. The authored link stays as it is,
        // so the 'pending' marker (and the space CSS reserves for it) comes
        // off rather than lingering on a link that will never become a card.
        if (!product || !link.isConnected) {
          delete link.dataset.signalProductLink;
          return;
        }

        this.replace(link, this.buildCard(product, path));
      })
      .catch(() => {
        // Same outcome as a miss: the link the merchant wrote is still there.
        delete link.dataset.signalProductLink;
      });
  }

  /**
   * The same-origin path for a product link, or null if the href is not one.
   * The origin the link was authored with is discarded on purpose — see the
   * file header.
   *
   * @param {string} href
   * @returns {string | null}
   */
  productPath(href) {
    let url;

    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return null;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!this.shopHosts.has(url.host.toLowerCase())) return null;

    const segments = url.pathname.split('/').filter(Boolean);
    const index = segments.indexOf('products');
    if (index === -1) return null;

    const handle = segments[index + 1];
    if (!handle || !HANDLE_PATTERN.test(handle)) return null;

    // Built from this page's own locale root rather than from the authored
    // path, so a link written against the primary domain still resolves under
    // a market prefix.
    return `${this.rootUrl.replace(/\/$/, '')}/products/${handle}`;
  }

  /**
   * @param {string} path
   * @returns {Promise<ProductJson | null>}
   */
  fetchProduct(path) {
    const pending = this.requests.get(path);
    if (pending) return pending;

    const request = fetch(`${path}.js`, {
      signal: this.abortController?.signal,
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    this.requests.set(path, request);

    return request;
  }

  /**
   * A div rather than one big anchor, because an unbuyable card carries a button
   * and a button inside an anchor is invalid markup that swallows its own
   * clicks. The title's stretched pseudo-element keeps the whole card one click
   * target anyway — the arrangement .signal-pcard already uses on the collection
   * grid, and the reason the same shape is built whether or not the button is
   * there: one component, one DOM.
   *
   * @param {ProductJson} product
   * @param {string} path
   * @returns {HTMLElement}
   */
  buildCard(product, path) {
    const unavailable = product.available === false;

    const card = document.createElement('div');
    card.className = 'signal-product-link__card';
    card.dataset.signalProductLink = 'card';

    const image = product.featured_image
      ? this.buildMedia(product.featured_image)
      : null;

    if (image) {
      card.appendChild(image);
    } else {
      card.classList.add('signal-product-link__card--no-image');
    }

    const body = document.createElement('span');
    body.className = 'signal-product-link__body';

    const kicker = document.createElement('span');
    kicker.className = 'signal-product-link__kicker';
    kicker.textContent = this.dataset.signalKicker || 'Also available';
    body.appendChild(kicker);

    const title = document.createElement('a');
    title.className = 'signal-product-link__title';
    title.href = path;
    title.textContent = product.title;
    body.appendChild(title);

    // A buyable sibling still ahead of its street date says so too, under the
    // title where an unbuyable one says its state.
    if (unavailable || this.releaseFor(path)) {
      const state = document.createElement('span');
      state.className = 'signal-product-link__state';
      state.textContent = this.stateLabel(path);
      body.appendChild(state);
    }

    card.appendChild(body);
    card.appendChild(
      unavailable ? this.buildNotify(product, path) : this.buildPrice(product)
    );

    return card;
  }

  /**
   * The release entry for a product path, or null when it is an ordinary sold-out
   * sibling.
   *
   * @param {string} path
   * @returns {[string, string] | null}
   */
  releaseFor(path) {
    const handle = path.split('/').filter(Boolean).pop() ?? '';
    const entry = this.releases[handle];

    return Array.isArray(entry) && entry.length === 2 ? entry : null;
  }

  /**
   * @param {string} path
   * @returns {string}
   */
  stateLabel(path) {
    const release = this.releaseFor(path);

    if (release) {
      return (this.dataset.signalReleaseLabel || 'Releases [date]').replace('[date]', release[1]);
    }

    return this.dataset.signalSoldOutLabel || 'Sold out';
  }

  /**
   * The notify-me trigger. It carries the same data attributes
   * snippets/signal-notify-trigger.liquid writes for a collection card, so
   * assets/signal-notify.js opens the same dialog with the same email body from
   * either surface without knowing which one raised it.
   *
   * @param {ProductJson} product
   * @param {string} path
   * @returns {HTMLElement}
   */
  buildNotify(product, path) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'signal-product-link__notify';
    button.textContent = this.dataset.signalNotifyLabel || 'Notify me';

    const release = this.releaseFor(path);
    const variant = product.variants?.[0];

    button.dataset.signalNotify = release ? 'release' : 'restock';
    button.dataset.signalNotifyTitle = product.title;
    button.dataset.signalNotifySku = variant?.sku ?? '';
    button.dataset.signalNotifyVariant = variant?.id == null ? '' : String(variant.id);
    button.dataset.signalNotifyUrl = `${this.shopUrl}${path}`;

    const thumbnail = product.featured_image
      ? this.thumbnailUrl(product.featured_image, THUMBNAIL_WIDTH)
      : null;
    if (thumbnail) button.dataset.signalNotifyImage = thumbnail;

    if (release) {
      button.dataset.signalNotifyRelease = release[0];
      button.dataset.signalNotifyReleaseLabel = release[1];
    }

    return button;
  }

  /**
   * @param {string} source - Shopify's protocol-relative CDN URL.
   * @returns {HTMLElement | null}
   */
  buildMedia(source) {
    const src = this.thumbnailUrl(source, THUMBNAIL_WIDTH);
    if (!src) return null;

    const media = document.createElement('span');
    media.className = 'signal-product-link__media';

    const image = document.createElement('img');
    image.className = 'signal-product-link__image';
    image.src = src;

    const retina = this.thumbnailUrl(source, THUMBNAIL_WIDTH * 2);
    if (retina) image.srcset = `${src} 1x, ${retina} 2x`;

    // Decorative: the title it belongs to is the next thing read out.
    image.alt = '';
    image.width = THUMBNAIL_WIDTH;
    image.height = THUMBNAIL_WIDTH;
    image.loading = 'lazy';
    image.decoding = 'async';

    media.appendChild(image);

    return media;
  }

  /**
   * @param {string} source
   * @param {number} width
   * @returns {string | null}
   */
  thumbnailUrl(source, width) {
    try {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', String(width));

      return url.href;
    } catch (error) {
      return null;
    }
  }

  /**
   * Only ever built for something that can be bought. A product that cannot gets
   * its state and a way to be told instead — a price on something unbuyable
   * reads as an offer.
   *
   * @param {ProductJson} product
   * @returns {HTMLElement}
   */
  buildPrice(product) {
    const price = document.createElement('span');
    price.className = 'signal-product-link__price';

    const amount = this.formatMoney(product.price);
    price.textContent = product.price_varies
      ? `${this.dataset.signalFromLabel || 'From'} ${amount}`
      : amount;

    return price;
  }

  /**
   * The JSON price is in the shop's minor units; the currency code comes from
   * Liquid rather than being assumed here.
   *
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    const amount = cents / 100;
    const options = this.currency
      ? { style: 'currency', currency: this.currency }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

    // A page lang Intl does not recognise throws rather than falling back, so
    // the shop's own locale is tried first and the browser's after it.
    for (const locale of [document.documentElement.lang || undefined, undefined]) {
      try {
        return new Intl.NumberFormat(locale, options).format(amount);
      } catch (error) {
        // Try the next one.
      }
    }

    return amount.toFixed(2);
  }

  /**
   * Swaps the card in for the link — or for the wrapper the link is the only
   * content of, so a link on its own line takes its empty paragraph with it
   * rather than leaving one behind.
   *
   * @param {HTMLAnchorElement} link
   * @param {HTMLElement} card
   */
  replace(link, card) {
    const container = link.closest(SCOPE_SELECTOR);

    /** @type {Element} */
    let node = link;

    while (
      node.parentElement &&
      node.parentElement !== container &&
      UNWRAPPABLE.has(node.parentElement.tagName) &&
      isSoleContent(node)
    ) {
      node = node.parentElement;
    }

    node.replaceWith(card);
  }
}

/**
 * True when everything else in the parent is whitespace or a comment.
 *
 * @param {Element} node
 * @returns {boolean}
 */
function isSoleContent(node) {
  const parent = node.parentElement;
  if (!parent) return false;

  return [...parent.childNodes].every(
    (child) =>
      child === node ||
      child.nodeType === Node.COMMENT_NODE ||
      (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim())
  );
}

if (!customElements.get('signal-product-link')) {
  customElements.define('signal-product-link', SignalProductLink);
}
